<template>
  <b-modal
    :id="modalId"
    v-model="showModal"
    :visible="showModal"
    centered
    size="md"
    footer-class="modal-footer--centered"
    @hidden="onModalHidden"
  >
    <template #modal-title>
      <span class="modal-title-text">{{ title }}</span>
    </template>
    <div class="d-flex flex-column px-2">
      <div class="form-group mb-4">
        <label for="rechnungsnummer">Rechnungsnummer eingeben</label>
        <b-form-input
          id="rechnungsnummer"
          v-model="rechnungsnummer"
          placeholder=""
          @keyup.enter="onFreigeben"
        ></b-form-input>
      </div>

      <!-- Manuelle Auswahl der Teilbeträge nur bei Gruppen (mehrere Teilbeträge) -->
      <div v-if="isGruppe" class="form-group mb-3">
        <label class="d-block mb-2">Teilbeträge für den Freigabebetrag auswählen</label>
        <div class="teilbetraege-chips">
          <button
            v-for="(teilbetrag, idx) in teilbetraege"
            :key="idx"
            type="button"
            class="teilbetrag-chip"
            :class="{ 'teilbetrag-chip--selected': isTeilbetragSelected(idx) }"
            @click="toggleTeilbetrag(idx)"
          >
            {{ formatBetrag(teilbetrag.betrag) }} {{ waehrung }}
          </button>
        </div>
      </div>

      <div class="form-group mb-3">
        <label for="kommentar">Kommentar (optional)</label>
        <b-form-textarea
          id="kommentar"
          v-model="kommentar"
          placeholder="Kommentar eingeben ..."
          rows="3"
          no-resize
        ></b-form-textarea>
      </div>

      <div class="betrag-uebersicht">
        <div class="betrag-zeile">
          <span class="betrag-label">{{ hauptLabel }}</span>
          <strong class="betrag-wert">{{ formatBetrag(freigabebetrag) }} {{ waehrung }}</strong>
        </div>
        <div v-if="isGruppe" class="betrag-zeile betrag-zeile--gruppe">
          <span class="betrag-label">Gruppenbetrag:</span>
          <span class="betrag-wert">{{ formatBetrag(gruppenbetrag) }} {{ waehrung }}</span>
        </div>
        <div v-if="hatBisherigenFreigabebetrag" class="betrag-zeile betrag-zeile--bisher">
          <span class="betrag-label">Bisheriger Freigabebetrag:</span>
          <span class="betrag-wert">{{ formatBetrag(bisherigerFreigabebetrag) }} {{ waehrung }}</span>
        </div>
        <div v-if="isAnpassen" class="betrag-zeile betrag-zeile--differenz">
          <span class="betrag-label">Offener Differenzbetrag:</span>
          <span class="betrag-wert">{{ formatBetrag(offenerDifferenzbetrag) }} {{ waehrung }}</span>
        </div>
      </div>
    </div>

    <template #modal-footer>
      <b-button class="dialog-btn-secondary" @click="abbrechen">Abbrechen</b-button>
      <b-button class="dialog-btn" variant="primary" :disabled="!isFreigebenEnabled" @click="onFreigeben">
        {{ confirmLabel }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
export default {
  name: 'RepruLeistungFreigabeModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    selectedLeistungen: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: 'Freigabe der markierten Leistungen',
    },
    confirmLabel: {
      type: String,
      default: 'Freigeben',
    },
    modalId: {
      type: String,
      default: 'modal-repru-leistung-freigabe',
    },
    // 'freigabe' = initiale Freigabe, 'anpassen' = bestehende Freigabe anpassen (Differenzbetrag)
    mode: {
      type: String,
      default: 'freigabe',
      validator: value => ['freigabe', 'anpassen'].includes(value),
    },
  },
  data() {
    return {
      rechnungsnummer: '',
      kommentar: '',
      selectedTeilbetragIndices: [],
    };
  },
  computed: {
    showModal: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      },
    },
    isAnpassen() {
      return this.mode === 'anpassen';
    },
    // Manuelle Teilbetrag-Auswahl gilt nur bei der Einzel-Freigabe einer Gruppe
    einzelLeistung() {
      return this.selectedLeistungen.length === 1 ? this.selectedLeistungen[0] : null;
    },
    teilbetraege() {
      return this.einzelLeistung?.teilbetraege ?? [];
    },
    isGruppe() {
      return this.teilbetraege.length > 1;
    },
    gruppenbetrag() {
      return this.einzelLeistung?.betrag ?? 0;
    },
    bisherigerFreigabebetrag() {
      return this.einzelLeistung?.freigabebetrag ?? 0;
    },
    hatBisherigenFreigabebetrag() {
      return this.einzelLeistung != null && this.bisherigerFreigabebetrag > 0;
    },
    // Summe aller selektierten Leistungen (Standardfall, ohne manuelle Auswahl)
    gesamtBetrag() {
      return this.selectedLeistungen.reduce((sum, leistung) => sum + (leistung.betrag || 0), 0);
    },
    // Summe der angehakten Teilbeträge
    summeAusgewaehlterTeilbetraege() {
      return this.selectedTeilbetragIndices.reduce(
        (sum, idx) => sum + (this.teilbetraege[idx]?.betrag || 0),
        0
      );
    },
    // Neu ausgewählter (freizugebender) Betrag
    freigabebetrag() {
      return this.isGruppe ? this.summeAusgewaehlterTeilbetraege : this.gesamtBetrag;
    },
    // Beim Anpassen: bereits freigegebener Betrag + neu ausgewählter Betrag
    gesamtFreigabebetrag() {
      return this.isAnpassen ? this.bisherigerFreigabebetrag + this.freigabebetrag : this.freigabebetrag;
    },
    // Beim Anpassen: noch offener Differenzbetrag, ändert sich mit der Auswahl
    offenerDifferenzbetrag() {
      return this.gruppenbetrag - this.gesamtFreigabebetrag;
    },
    waehrung() {
      return this.selectedLeistungen.length > 0 ? this.selectedLeistungen[0].waehrung || 'EUR' : 'EUR';
    },
    hauptLabel() {
      if (this.isAnpassen) return 'Freizugebender Betrag:';
      return this.isGruppe ? 'Freigabebetrag:' : 'Freizugebender Betrag:';
    },
    isFreigebenEnabled() {
      if (this.rechnungsnummer.trim() === '') return false;
      return true;
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.initTeilbetragSelection();
      } else {
        this.resetModal();
      }
    },
  },
  methods: {
    initTeilbetragSelection() {
      // Standardmäßig ist kein Teilbetrag ausgewählt
      this.selectedTeilbetragIndices = [];
    },
    isTeilbetragSelected(idx) {
      return this.selectedTeilbetragIndices.includes(idx);
    },
    toggleTeilbetrag(idx) {
      if (this.isTeilbetragSelected(idx)) {
        this.selectedTeilbetragIndices = this.selectedTeilbetragIndices.filter(i => i !== idx);
      } else {
        this.selectedTeilbetragIndices = [...this.selectedTeilbetragIndices, idx];
      }
    },
    abbrechen() {
      this.showModal = false;
    },
    onModalHidden() {
      this.resetModal();
    },
    resetModal() {
      this.rechnungsnummer = '';
      this.kommentar = '';
      this.selectedTeilbetragIndices = [];
    },
    onFreigeben() {
      if (!this.isFreigebenEnabled) return;

      this.$emit('freigeben', {
        rechnungsnummer: this.rechnungsnummer,
        kommentar: this.kommentar.trim(),
        leistungen: this.selectedLeistungen,
        // Es wird nur der neu ausgewählte Betrag übergeben (nicht der bereits freigegebene Betrag)
        freigabebetrag: this.isGruppe ? this.roundCents(this.freigabebetrag) : null,
      });

      this.showModal = false;
    },
    roundCents(betrag) {
      return Math.round((betrag || 0) * 100) / 100;
    },
    formatBetrag(betrag) {
      if (betrag == null) return '0,00';
      return betrag.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped>
.modal-title-text {
  font-size: 17px;
  font-weight: 600;
}

.betrag-uebersicht {
  margin-top: 4px;
}

.betrag-zeile {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  font-size: 1.15rem;
}

.betrag-zeile + .betrag-zeile {
  margin-top: 2px;
}

.betrag-wert {
  font-variant-numeric: tabular-nums;
}

.betrag-zeile--gruppe,
.betrag-zeile--bisher {
  font-size: 0.95rem;
  color: #5e6278;
}

.betrag-zeile--differenz {
  font-weight: 600;
  color: var(--text);
}

.teilbetraege-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.teilbetrag-chip {
  display: inline-flex;
  align-items: center;
  height: var(--control-h-sm);
  padding: 0 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 13px;
  font-weight: 500;
  color: var(--text-body);
  /* Beträge stehen in der Zeile untereinander */
  font-variant-numeric: tabular-nums;
  cursor: pointer;
}

.teilbetrag-chip:hover {
  background: var(--surface-hover);
}

.teilbetrag-chip--selected {
  background: var(--primary-light);
  border-color: var(--primary);
  color: var(--primary);
}
</style>
